import { Injectable } from '@angular/core';
import { SharedService } from './shared.service';
import { Observable, of, Subject, throwError } from 'rxjs';
import {
  delay,
  filter,
  map,
  mergeMap,
  share,
  switchMap,
  take,
} from 'rxjs/operators';
import { Device } from '@ionic-native/device/ngx';

declare const window: any;

@Injectable({ providedIn: 'root' })
export class DesktopService {
  private _isDesktop = false;
  get isDesktop() {
    return this._device.model == 'desktop';
  }

  private desktopMessageSubject: Subject<{
    Method: string;
    Data: any;
    Error: string;
    RequestId?: string;
  }> = new Subject<{ Method: string; Data: any; Error: string }>();

  constructor(
    private _shared: SharedService,
    private _device: Device,
  ) {
    this._initDesktopConnection();
  }

  on(method: string, handleError = true): Observable<any> {
    return this.desktopMessageSubject.asObservable().pipe(
      filter((res) => res.Method == method),
      take(1),
      switchMap((res) => {
        if (res.Error) {
          if (handleError) {
            this._shared.showErrorToast(res.Error);
          }

          return throwError(res.Error);
        } else {
          return of(res.Data);
        }
      }),
    );
  }

  serialPortReceiver(portName: string) {
    return this.desktopMessageSubject.asObservable().pipe(
      share(),
      filter(
        (res) =>
          res.Method == 'serialPort' && res.RequestId == portName && res.Data,
      ),
      mergeMap((res) => {
        if (res.Error) {
          return throwError(() => res.Error);
        } else {
          return of(res.Data);
        }
      }),
    );
  }

  getConnectedPrinters() {
    this.send({ Method: 'getPrinters' });
    return this.on('getPrinters').pipe(
      map((res) => {
        return res.Message;
      }),
    );
  }

  getAvailablePrinters() {
    this.send({ Method: 'getAvailablePrinters' });
    return this.on('getAvailablePrinters', false).pipe(
      map((res) => {
        return res.Message;
      }),
    );
  }

  serialPort(
    portName: string,
    baudRate: number,
    parity: number,
    data?: string,
  ): Observable<string> {
    this.send({
      Method: 'serialPort',
      portName: portName,
      data: data,
      baudRate,
      parity,
    });
    return this.on('serialPort').pipe(
      map((res) => {
        return res.Message;
      }),
    );
  }

  serialPortClose(portName: string) {
    this.send({ Method: 'serialPortClose', portName: portName });
  }

  getActivePorts(): Observable<string[]> {
    this.send({ Method: 'getActivePorts' });
    return this.on('getActivePorts').pipe(
      delay(500),
      map((res) => {
        return res.Message;
      }),
    );
  }

  send(data: any) {
    window.chrome.webview.postMessage(data);
  }

  clearCache() {
    // !! this action reset deviceId  
    // if (window.chrome?.webview) {
    //   this.send({ Method: 'clearBrowserCache' });
    // }
  }

  resetCache(){
    // !! this action reset deviceId  
    if (window.chrome?.webview) {
      this.send({ Method: 'clearBrowserCache' });
    }
  }

  checkIpAddressAndPort(ipAddress: string, port: number): Observable<boolean> {
    this.send({
      Method: 'checkIpAddressAndPort',
      IpAddress: ipAddress,
      Port: port,
    });
    return this.on('checkIpAddressAndPort').pipe(
      map((res) => {
        return res.Message;
      }),
    );
  }

  netPrinterPrint(ipAddress: string, port: number, data: string) {
    this.send({
      Method: 'netPrinterPrint',
      IpAddress: ipAddress,
      Port: port,
      Data: data,
    });
    return this.on('netPrinterPrint', false).pipe(
      map((res) => {
        return res.Message;
      }),
    );
  }

  /**
   * Proxy TCP send/receive via desktop host (bank terminal ECR JSON + 0x00 framing).
   * @param commandBase64 raw bytes already framed by POS (base64)
   * @returns response raw bytes as base64
   */
  socketSend(
    ipAddress: string,
    port: number,
    commandBase64: string,
    timeout = 60000
  ): Observable<string> {
    this.send({
      Method: 'socketSend',
      IpAddress: ipAddress,
      Port: port,
      Command: commandBase64,
      Timeout: timeout,
    });
    return this.on('socketSend', false).pipe(
      map((res) => {
        return res.Message;
      }),
    );
  }

  socketDisconnect() {
    this.send({ Method: 'socketDisconnect' });
  }

  fullScreenOn() {
    this.send({ Method: 'fullScreenOn' });
  }

  fullScreenOff() {
    this.send({ Method: 'fullScreenOff' });
  }

  openAdminApp() {
    this.send({ Method: 'openAdminApp' });
  }

  private _initDesktopConnection() {
    if (window.chrome && window.chrome.webview) {
      window.chrome.webview.addEventListener('message', (event: any) => {
        this.desktopMessageSubject.next(event?.data);
      });
    }
  }
}
